import * as Sentry from '@sentry/react';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { onIdTokenChanged } from 'firebase/auth';
import { ClientError } from 'graphql-request';
import type React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '@/core/providers/AuthFirebaseProvider';
import { initializeGraphQLClient } from './graphqlClient';

interface GraphQLClientProviderProps {
  token: string;
  role: string;
  userDataParams: UserDataParams;
  children: React.ReactNode;
}

interface GraphQLClientContextType {
  token: string;
  role: string;
  userDataParams: UserDataParams;
}

const GraphQLClientContext = createContext<GraphQLClientContextType | undefined>(undefined);

export const GraphQLClientProvider: React.FC<GraphQLClientProviderProps> = ({
  token,
  role,
  userDataParams,
  children,
}) => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    let cachedToken: string | null = null;
    let cachedExpirationTime: number | null = null;

    const getLatestToken = async (): Promise<string> => {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No authenticated user found');
      }

      const currentTime = Date.now();

      if (
        cachedToken &&
        cachedExpirationTime &&
        cachedExpirationTime - currentTime > 5 * 60 * 1000
      ) {
        return cachedToken;
      }

      const result = await user.getIdTokenResult();
      cachedToken = result.token;
      cachedExpirationTime = new Date(result.expirationTime).getTime();

      return cachedToken;
    };

    initializeGraphQLClient(token, role, userDataParams, getLatestToken);
    setIsInitialized(true);

    const checkTokenExpirationAndRefresh = async () => {
      const user = auth.currentUser;
      if (!user || !cachedExpirationTime) return;

      try {
        const currentTime = Date.now();
        const timeUntilExpiry = cachedExpirationTime - currentTime;

        if (timeUntilExpiry <= 10 * 60 * 1000) {
          console.log(
            `Token expiring in ${Math.floor(timeUntilExpiry / 1000 / 60)} minutes, refreshing...`
          );
          const newToken = await user.getIdToken(true);
          cachedToken = newToken;
          cachedExpirationTime = currentTime + 60 * 60 * 1000;
          initializeGraphQLClient(newToken, role, userDataParams, getLatestToken);
        }
      } catch (error) {
        console.error('Error checking token expiration:', error);
      }
    };

    const intervalId = setInterval(checkTokenExpirationAndRefresh, 30 * 1000);

    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      if (!user) return;

      try {
        const newToken = await user.getIdToken();
        cachedToken = newToken;
        cachedExpirationTime = Date.now() + 60 * 60 * 1000;
        initializeGraphQLClient(newToken, role, userDataParams, getLatestToken);
        console.log('Token refreshed via onIdTokenChanged');
      } catch (error) {
        console.error('Error refreshing token on change:', error);
      }
    });

    return () => {
      clearInterval(intervalId);
      unsubscribe();
    };
  }, [token, role, userDataParams]);

  if (!isInitialized) {
    return <div>Loading...</div>;
  }

  return (
    <GraphQLClientContext.Provider value={{ token, role, userDataParams }}>
      <QueryClientProvider client={queryClient}>
        {children}
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </GraphQLClientContext.Provider>
  );
};

function shouldCaptureQueryError(error: Error): boolean {
  if (!Sentry.isInitialized()) {
    return false;
  }

  if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
    return false;
  }

  if (error instanceof ClientError) {
    const response = error.response;

    if (response?.status === 401 || response?.status === 403) {
      return false;
    }

    const firstError = response?.errors?.[0];
    if (firstError?.extensions?.code) {
      const code = firstError.extensions.code as string;
      const ignoredCodes = ['GRAPHQL_VALIDATION_FAILED', 'permission-denied', 'access-denied'];
      if (ignoredCodes.some((ignored) => code.toLowerCase().includes(ignored.toLowerCase()))) {
        return false;
      }
    }
  }

  if (error instanceof ClientError && error.response?.status && error.response.status >= 500) {
    return true;
  }

  return true;
}

export const queryCache = new QueryCache({
  onError: (error, query) => {
    if (query.meta?.skipSentryReport) {
      return;
    }

    if (shouldCaptureQueryError(error as Error)) {
      Sentry.captureException(error, {
        contexts: {
          reactQuery: {
            queryKey: JSON.stringify(query.queryKey),
            queryHash: query.queryHash,
            state: query.state.status,
            type: query.options.queryFn ? 'query' : 'unknown',
          },
        },
        tags: {
          errorType: 'react-query',
        },
      });
    }
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      throwOnError: false,
    },
    mutations: {
      throwOnError: false,
    },
  },
  queryCache,
});

export const useGraphQLClient = () => {
  const context = useContext(GraphQLClientContext);
  if (context === undefined) {
    throw new Error('useGraphQLClient must be used within a GraphQLClientProvider');
  }
  return context;
};
